import React from 'react';
import { useAuth } from '../AuthContext';
import { calculateDistance } from '../utils/geo';
import { toast } from 'react-toastify';

const MeetupCard = ({ session, onViewOnMap, onOpenChat, onDelete, onEdit, onLeave, onJoin, userLocation, onViewProfile }) => {
    const { user } = useAuth();
    const isParticipant = (session.participants || []).includes(user?.uid);
    const isHost = session.uid === user?.uid;

    // Calculate distance
    let distanceFromUser = null;
    let isTooFar = false;

    if (userLocation && session.location) {
        let lat, lng;
        if (typeof session.location === 'string') {
            [lat, lng] = session.location.split(',').map(Number);
        } else if (session.location.coordinates) {
            [lat, lng] = session.location.coordinates.split(',').map(Number);
        } else if (session.location.latitude) {
            lat = session.location.latitude;
            lng = session.location.longitude;
        }
        
        if (lat && lng && !isNaN(lat)) {
            distanceFromUser = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
            if (distanceFromUser > 50) {
                isTooFar = true;
            }
        }
    }

    const handleShare = (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}/?id=${session.id}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success("Link copied to clipboard!");
        }).catch(() => {
            toast.error("Failed to copy link");
        });
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md mb-3">
            <div className="flex justify-between items-start">
                <div className="flex gap-3">
                    <img
                        src={session.photoURL || `https://ui-avatars.com/api/?name=${session.displayName}&background=random`}
                        alt={session.displayName}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                        referrerPolicy="no-referrer"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onViewProfile) onViewProfile(session.uid);
                        }}
                    />
                    <div>
                        <h3
                            className="font-bold text-gray-800 text-lg leading-tight cursor-pointer hover:text-indigo-600 transition-colors flex items-center gap-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onViewProfile) onViewProfile(session.uid);
                            }}
                        >
                            {session.name}
                            {session.type === 'now' && (
                                <span className="relative flex h-3 w-3 flex-shrink-0" title="Live Now">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                </span>
                            )}
                        </h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                            📍 {isTooFar ? <span className="text-gray-400 italic">Location hidden (too far)</span> : (session.address || 'Location not provided')}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleShare}
                        className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 transition-colors"
                        title="Share Link"
                    >
                        🔗
                    </button>
                    {isHost && onEdit && (
                        <button
                            onClick={() => onEdit(session)}
                            className="text-indigo-500 hover:text-indigo-700 text-xs font-semibold px-2 py-1 bg-indigo-50 rounded"
                            title="Edit Meetup"
                        >
                            Edit
                        </button>
                    )}
                    {isHost && onDelete && (
                        <button
                            onClick={() => onDelete(session.id)}
                            className="text-red-500 hover:text-red-700 text-xs font-semibold px-2 py-1 bg-red-50 rounded"
                            title="Delete Meetup"
                        >
                            Delete
                        </button>
                    )}
                </div>
            </div>

            <div className="text-sm text-gray-600 mt-3 grid grid-cols-2 gap-2">
                <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-500 text-xs uppercase tracking-wide">Skill</span>
                    <p className="capitalize">{session.skillLevel}</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-500 text-xs uppercase tracking-wide">Type</span>
                    <p className="capitalize">{session.gameType}</p>
                </div>
            </div>

            <div className="mt-2 flex items-center gap-3">
                {session.isKidFriendly && (
                    <span className="text-green-600 text-xs font-semibold bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
                        ✓ Kid-Friendly
                    </span>
                )}
                {session.isBeginnerFriendly && (
                    <span className="text-teal-600 text-xs font-semibold bg-teal-50 px-2 py-1 rounded-full flex items-center gap-1">
                        🌱 Beginner-Friendly
                    </span>
                )}
                <span className="text-indigo-600 text-xs font-semibold bg-indigo-50 px-2 py-1 rounded-full flex items-center gap-1">
                    ♟️ Board Provided
                </span>
                <span className="text-blue-600 text-xs font-semibold bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1">
                    👥 {(session.participants || []).length} Joined
                </span>
            </div>

            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                {!isTooFar ? (
                    <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(session.address || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-50 transition-colors text-center no-underline flex justify-center items-center gap-1"
                    >
                        📍 Navigate
                    </a>
                ) : (
                    <button disabled className="flex-1 bg-gray-100 border border-gray-200 text-gray-400 px-3 py-1.5 rounded text-sm font-medium cursor-not-allowed">
                        📍 Navigate
                    </button>
                )}

                <button
                    onClick={() => onViewOnMap(session)}
                    className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                    View Map
                </button>

                {onOpenChat && (isHost || isParticipant) && !isTooFar && (
                    <button
                        onClick={() => onOpenChat(session)}
                        className="flex-1 bg-indigo-50 text-indigo-600 border border-indigo-100 px-3 py-1.5 rounded text-sm font-medium hover:bg-indigo-100 transition-colors"
                    >
                        Chat
                    </button>
                )}
                {onOpenChat && (isHost || isParticipant) && isTooFar && (
                    <button
                        disabled
                        title="Too far to chat"
                        className="flex-1 bg-gray-50 text-gray-400 border border-gray-200 px-3 py-1.5 rounded text-sm font-medium cursor-not-allowed"
                    >
                        Chat
                    </button>
                )}

                {/* Join / Leave Actions */}
                {!isHost && !isParticipant && onJoin && (
                    <button
                        onClick={() => onJoin(session.id)}
                        disabled={isTooFar}
                        title={isTooFar ? "Must be within 50 miles to join" : ""}
                        className={`flex-1 px-3 py-1.5 rounded text-sm font-medium shadow-sm transition-colors ${isTooFar
                            ? 'bg-gray-300 text-white cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                    >
                        {isTooFar ? 'Too Far' : 'Join'}
                    </button>
                )}

                {!isHost && isParticipant && onLeave && (
                    <button
                        onClick={() => onLeave(session.id)}
                        className="flex-1 bg-yellow-50 text-yellow-700 border border-yellow-200 px-3 py-1.5 rounded text-sm font-medium hover:bg-yellow-100 transition-colors"
                    >
                        Leave
                    </button>
                )}
            </div>
            {session.availableUntil && (
                <p className="text-xs text-center text-gray-400 mt-2">
                    Available until: {new Date(session.availableUntil.toDate ? session.availableUntil.toDate() : session.availableUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
            )}
        </div>
    );
};

export default MeetupCard;
